import { useTranslation } from 'react-i18next';
import LoaderIcon from '@/public/icons/approval/loader.svg';
import CheckIcon from '@/public/icons/approval/check.svg';
import AlertIcon from '@/public/icons/alert.svg';
import StatusBadgeBase from '@/components/Approval/ApprovalView/StatusBadgeBase';
import { DOCUMENT_STATUS_CONFIG } from '@/components/Approval/ApprovalView/statusMapping';
import { APPROVAL_STATUS_BADGE } from '@/components/Approval/_tokens/statusColors';
import type { StatusBadgePrefix } from '@/components/Approval/ApprovalView/statusMapping';
import type { DocumentStatus } from '@/components/Approval/ApprovalView/types';

/**
 * 전자결재 공통 문서 상태 딱지.
 * 매핑 불가 코드/빈값은 렌더 생략 — 목록에 raw 상태 코드가 노출되지 않게.
 */
const PREFIX_ICON: Partial<
  Record<
    NonNullable<StatusBadgePrefix>,
    React.ComponentType<React.SVGProps<SVGSVGElement>>
  >
> = {
  asterisk: LoaderIcon,
  check: CheckIcon,
  warning: AlertIcon,
};

type ApprovalStatusBadgeProps = {
  status?: string | null;
  showIcon?: boolean;
};

const ApprovalStatusBadge = ({
  status,
  showIcon = true,
}: ApprovalStatusBadgeProps) => {
  const { t } = useTranslation();
  if (!status) return null;
  // '삭제'(D)는 결재 상태값이 아님 — 딱지 미노출.
  if (status === 'D') return null;
  const config = DOCUMENT_STATUS_CONFIG[status as DocumentStatus];
  if (!config) return null;

  const Icon =
    showIcon && config.prefix ? PREFIX_ICON[config.prefix] : undefined;
  const iconNode = Icon ? (
    <Icon width={16} height={16} aria-hidden />
  ) : undefined;

  return (
    <StatusBadgeBase
      variant="tag"
      tone={config.tone}
      label={t(config.label, { context: 'status' })}
      palette={APPROVAL_STATUS_BADGE[config.tone]}
      customPrefix={iconNode}
    />
  );
};

export default ApprovalStatusBadge;
